// packages/ui/src/Spinner.tsx
import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

export interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The size of the spinner. Corresponds to Tailwind CSS w-X/h-X classes.
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /**
   * Optional text to display below the spinner.
   */
  text?: string;
  /**
   * i18next key for the text.
   */
  textKey?: string;
  /**
   * Additional Tailwind CSS classes for the spinner container.
   */
  className?: string;
  /**
   * Additional Tailwind CSS classes for the spinner circle itself.
   */
  spinnerClassName?: string;
  /**
   * Additional Tailwind CSS classes for the text.
   */
  textClassName?: string;
}

export const Spinner = React.forwardRef<HTMLDivElement, SpinnerProps>(
  ({
    size = 'md',
    text,
    textKey,
    className,
    spinnerClassName,
    textClassName,
    ...props
  }, ref) => {
    const { t } = useTranslation();
    const resolvedText = textKey ? t(textKey) : text;

    const sizeClasses = {
      sm: { spinner: 'w-6 h-6 border-2', text: 'text-xs mt-1' },
      md: { spinner: 'w-8 h-8 border-2', text: 'text-sm mt-2' },
      lg: { spinner: 'w-10 h-10 border-4', text: 'text-base mt-2' },
      xl: { spinner: 'w-12 h-12 border-4', text: 'text-lg mt-3' },
      '2xl': { spinner: 'w-16 h-16 border-4', text: 'text-xl mt-4' },
    };

    return (
      <div
        ref={ref}
        className={clsx('flex flex-col items-center justify-center', className)}
        role="status"
        aria-live={resolvedText ? 'polite' : 'off'}
        {...props}
      >
        <div
          className={clsx(
            'animate-spin rounded-full border-black border-t-emerald-500', // Basic spinner styling
            '[.theme-liquid-glass_&]:border-white/30 [.theme-liquid-glass_&]:border-t-violet-400',
            sizeClasses[size].spinner,
            spinnerClassName
          )}
          aria-hidden={resolvedText ? 'true' : 'false'} // Hide from screen readers if text provides status
        />
        {resolvedText && (
          <p
            className={clsx(
              'text-gray-700 font-medium',
              '[.theme-liquid-glass_&]:text-white/80',
              sizeClasses[size].text,
              textClassName
            )}
            id={props['aria-labelledby'] || props.id ? `${props.id}-text` : undefined} // Link text to spinner if ids present
          >
            {resolvedText}
          </p>
        )}
        {/* Hidden text for screen readers if no visible text is present but a status is desired */}
        {!resolvedText && (
          <span className="sr-only">Loading...</span>
        )}
      </div>
    );
  }
);

Spinner.displayName = 'Spinner';
